import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Alert
} from '@mui/material';
import { PaymentService } from '../services/PaymentService';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

function PurchaseHistory() {
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (keycloak && keycloak.authenticated) {
      PaymentService.keycloak = keycloak;
      loadTransactions();
    }
  }, [keycloak]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const userId = keycloak.tokenParsed.sub;
      const response = await PaymentService.getClientTransactions(userId);

      if (Array.isArray(response)) {
        const sorted = response.sort((a, b) =>
          new Date(b.appointmentTime) - new Date(a.appointmentTime)
        );
        setTransactions(sorted);
      } else {
        setTransactions([]);
      }
      setError(null);
    } catch (err) {
      console.error('Error loading purchase history:', err);
      setError('Failed to load purchase history');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (!keycloak.authenticated) {
    return (
      <Container>
        <Typography variant="h6">Please log in to view your purchase history.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Helmet>
        <title>Purchase History - Pair SelfCare</title>
      </Helmet>
      <Typography variant="h4" gutterBottom>
        Purchase History
      </Typography>

      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && transactions.length === 0 && (
        <Alert severity="info">
          You have no purchase history yet.
        </Alert>
      )}

      {!loading && transactions.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Service</strong></TableCell>
                <TableCell><strong>Provider</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id} hover>
                  <TableCell>{formatDate(transaction.appointmentTime)}</TableCell>
                  <TableCell>{transaction.service || 'N/A'}</TableCell>
                  <TableCell>{transaction.providerName || 'N/A'}</TableCell>
                  <TableCell align="right">{formatCurrency(transaction.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}

export default PurchaseHistory;
